import * as React from 'react';
import * as I18n from 'i18n-js';
import StreetAddressFormatHint from './street_address_format_hint';
import bind from '@reverbdotcom/commons/dist/bind';
import { Countries, core_apimessages_Address } from '../../../schemas/rql';
import { ICountry } from '../../../schemas/core-schema';
import ControlledInput from '@reverbdotcom/commons/dist/components/controlled_input';
import { CountrySelector } from '../../discovery/country_selector';
import { decamelizeKeys } from 'humps';

interface IProps {
  name?: string;
  address: core_apimessages_Address;
  addressTitle: string;
  countriesForSelect: Countries._Countries[];
  children?: any;
  phoneInputRequired: boolean;
  onUpdate(attributes: core_apimessages_Address);
  showPhone?: boolean;
  buildInputName?: (field: string) => string;
}

interface IState {
  streetAddressForHint: string;
}

export default class AddressFields extends React.Component<IProps, IState> {
  static defaultProps = {
    showPhone: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      streetAddressForHint: '',
    };
  }

  decamelizedCountries() {
    return this.props.countriesForSelect.map(c => decamelizeKeys(c)) as ICountry[];
  }

  regionSelected(regionInput) {
    const region = regionInput ? regionInput.value : '';
    this.updateAddress({
      region,
    });
  }

  regionsForSelectedCountryCode() {
    const selectedCountry = this.props.countriesForSelect.find(country =>
      country.countryCode === this.props.address.countryCode,
    );

    if (selectedCountry && this.hasRegions(selectedCountry)) {
      return this.regionsForSelectWithBlank(selectedCountry.subregions.slice(0));
    }
    return [];
  }

  regionsForSelectWithBlank(regions) {
    return [{ code: '', name: '' }].concat(regions);
  }

  hasRegions(country) {
    return (country.subregions.length > 0);
  }

  noRegionsToSelect() {
    return (this.regionsForSelectedCountryCode().length === 0);
  }

  inputName(fieldName: string) {
    if (this.props.buildInputName) {
      return this.props.buildInputName(fieldName);
    }

    return fieldName;
  }

  regionsTextInput() {
    return (
      <ControlledInput
        inputType="text"
        required
        inputName={this.inputName('region')}
        fieldName="region"
        inputValue={this.props.address.region}
        updateField={this.updateAddress}
      />
    );
  }

  regionOptionsForSelect() {
    return (
      this.regionsForSelectedCountryCode().map(region =>
        <option
          key={region.code}
          value={region.code}
        >
          {region.name}
        </option>,
      )
    );
  }

  regionsField() {
    if (this.noRegionsToSelect()) { return this.regionsTextInput(); }

    return (
      <div className="styled-dropdown">
        <select
          autoComplete="region"
          required
          id="region_field"
          name={this.inputName('region')}
          value={this.props.address.region}
          onChange={event => this.regionSelected(event.target)}
        >
          {this.regionOptionsForSelect()}
        </select>
      </div>
    );
  }

  @bind
  updateAddress(attributes: Partial<core_apimessages_Address>) {
    this.props.onUpdate({ ...this.props.address, ...attributes });
  }

  @bind
  onCountryChange(countryCode) {
    const addressAttributes: Partial<core_apimessages_Address> = {
      countryCode,
      region: '',
    };
    this.updateAddress(addressAttributes);
  }

  selectedCountryCode() {
    return (this.props.address.countryCode || '');
  }

  phoneInput() {
    if (!this.props.showPhone) { return null; }

    return (
      <div className="form-group mb-0">
        <div className="form-group__header">
          <label htmlFor={this.inputName('phone')}>
            {I18n.t('discovery.addresses.addressForm.phone')}
          </label>
        </div>
        <div className="form-group__fields">
          <ControlledInput
            inputName={this.inputName('phone')}
            fieldName="phone"
            placeholder={I18n.t('discovery.addresses.addressForm.placeholder.phone')}
            required={this.props.phoneInputRequired}
            autoComplete="tel"
            inputType="text"
            inputValue={this.props.address.phone}
            updateField={this.updateAddress}
          />
        </div>
      </div>
    );
  }

  @bind
  onStreetAddressBlur(event) {
    this.setState({ streetAddressForHint: event.target.value });
  }

  nameInput() {
    return (
      <div className="form-group">
        <div className="form-group__header">
          <label htmlFor={this.inputName('name')}>
            {I18n.t('discovery.addresses.addressForm.name')}
          </label>
        </div>
        <div className="form-group__fields">
          <ControlledInput
            inputName={this.inputName('name')}
            fieldName="name"
            placeholder={I18n.t('discovery.addresses.addressForm.placeholder.name')}
            autoComplete="name"
            required
            inputType="text"
            inputValue={this.props.address.name}
            updateField={this.updateAddress}
          />
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.nameInput()}

        <div className="form-group">
          <div className="form-group__header">
            <label
              htmlFor={this.inputName('street_address')}
              data-address-title
            >
              {this.props.addressTitle}
            </label>
          </div>
          <div className="form-group__fields">
            <ControlledInput
              inputType="text"
              inputName={this.inputName('street_address')}
              fieldName="streetAddress"
              placeholder={I18n.t('discovery.addresses.addressForm.placeholder.streetAddress')}
              inputValue={this.props.address.streetAddress}
              required
              autoComplete="street-address"
              onBlur={this.onStreetAddressBlur}
              updateField={this.updateAddress}
            />
            <ControlledInput
              inputName={this.inputName('extended_address')}
              fieldName="extendedAddress"
              placeholder={I18n.t('discovery.addresses.addressForm.placeholder.extendedAddress')}
              autoComplete="street-address-2"
              inputType="text"
              inputValue={this.props.address.extendedAddress}
              updateField={this.updateAddress}
              className="mt-half"
            />
            <StreetAddressFormatHint
              streetAddress={this.state.streetAddressForHint}
            />
          </div>
        </div>

        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12">
            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor={this.inputName('locality')}>
                  {I18n.t('discovery.addresses.addressForm.city')}
                </label>
              </div>
              <div className="form-group__fields">
                <ControlledInput
                  inputName={this.inputName('locality')}
                  fieldName="locality"
                  placeholder={I18n.t('discovery.addresses.addressForm.placeholder.city')}
                  required
                  autoComplete="locality"
                  inputType="text"
                  inputValue={this.props.address.locality}
                  updateField={this.updateAddress}
                />
              </div>
            </div>
          </div>

          <div className="g-col-6 g-col-mobile-12">
            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor="region_field">
                  {I18n.t('discovery.addresses.addressForm.state')}
                </label>
              </div>
              <div className="form-group__fields">
                {this.regionsField()}
              </div>
            </div>
          </div>
        </div>

        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12">
            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor={this.inputName('postal_code')}>
                  {I18n.t('discovery.addresses.addressForm.zip_code')}
                </label>
              </div>
              <div className="form-group__fields">
                <ControlledInput
                  inputName={this.inputName('postal_code')}
                  fieldName="postalCode"
                  placeholder={I18n.t('discovery.addresses.addressForm.placeholder.zip_code')}
                  required
                  autoComplete="postal-code"
                  inputType="text"
                  inputValue={this.props.address.postalCode}
                  updateField={this.updateAddress}
                />
              </div>
            </div>
          </div>

          <div className="g-col-6 g-col-mobile-12">
            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor={this.inputName('country_code')}>
                  {I18n.t('discovery.addresses.addressForm.country')}
                </label>
              </div>
              <div className="form-group__fields">
                <div className="styled-dropdown">
                  <CountrySelector
                    id={this.inputName('country_code')}
                    inputName={this.inputName('country_code')}
                    countries={this.decamelizedCountries()}
                    onChange={this.onCountryChange}
                    value={this.selectedCountryCode()}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12">
            {this.phoneInput()}
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/shared/addresses/address_fields.tsx